import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import ArtworkCard from "@/components/ArtworkCard";

const DEFAULT_HERO_IMG =
  "https://customer-assets.emergentagent.com/job_aboriginal-art-store/artifacts/x12y4yha_image.png";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [artist, setArtist] = useState({ name: "", bio: "", statement: "", hero_image_path: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/artist-profile")])
      .then(([p, a]) => {
        setProducts(p.data);
        setArtist(a.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const heroSrc = artist.hero_image_path ? imageUrl(artist.hero_image_path) : DEFAULT_HERO_IMG;

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          {/* Painted warm wash behind everything */}
          <div className="absolute inset-0"
               style={{
                 background:
                   "radial-gradient(60% 80% at 80% 20%, rgba(232,181,58,0.45), transparent 60%)," +
                   "radial-gradient(50% 70% at 100% 100%, rgba(226,92,70,0.35), transparent 65%)," +
                   "radial-gradient(50% 60% at 10% 90%, rgba(123,91,166,0.18), transparent 65%)," +
                   "#F8F5F2",
               }}
          />
          <img
            src={heroSrc}
            alt="Painting by Colleen Prosser"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "center 30%" }}
          />
          {/* Left fade keeps the headline legible */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#F8F5F2] via-[#F8F5F2]/85 to-[#F8F5F2]/10 md:via-[#F8F5F2]/70 md:to-transparent" />
          {/* Long, soft fade to bg at the bottom so the image doesn't cut off */}
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#F8F5F2] via-[#F8F5F2]/80 via-40% to-transparent" />
          {/* Gentle fade at the very top so it blends into the header */}
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#F8F5F2]/60 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-16 md:pt-40 md:pb-24">
          <div className="max-w-3xl">
            <p className="label-eyebrow fade-up">Original Indigenous Australian Art</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] text-[#2D2926] mt-6 fade-up-delay-1">
              Paintings that carry the stories of country.
            </h1>
            <p className="text-lg text-[#5B5653] leading-relaxed mt-8 max-w-xl fade-up-delay-2">
              {artist.statement ||
                "Each work is a single, unrepeatable piece — hand-painted on canvas, signed, and delivered free anywhere in Australia."}
            </p>
            <div className="mt-12 flex flex-wrap gap-4 fade-up-delay-3">
              <Link to="/store" data-testid="hero-shop-btn" className="btn-primary">
                Browse Collection
              </Link>
              <Link to="/about" data-testid="hero-about-btn" className="btn-secondary">
                About the Artist
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Artist statement */}
      <section data-testid="artist-statement" className="wash-warm">
        <div className="max-w-4xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-20 md:pb-24">
          <div className="dot-row mb-8">
            <span className="dot dot--coral" />
            <span className="dot dot--gold" />
            <span className="dot dot--teal" />
            <span className="dot dot--plum" />
            <span className="dot dot--coral" />
          </div>
          <p className="label-eyebrow text-center">A Note from the Artist</p>
          <div className="mt-10 space-y-6 text-[#5B5653] leading-relaxed text-base md:text-lg text-center">
            <p>
              My artworks are created using acrylic paints on canvas, often incorporating
              textured elements and carefully placed rhinestones to add depth, movement, and
              light. Each piece is hand-painted from my home studio and inspired by my connection
              to Country, culture, storytelling, healing, waterways, animals, spirit, and the
              natural world.
            </p>
            <p>
              No two paintings are exactly the same. Every artwork is an original creation that
              carries its own story, meaning, and connection. Through colour, texture, symbols,
              and movement, I hope to share a sense of belonging, healing, and connection with
              those who welcome my art into their homes.
            </p>
          </div>
        </div>
      </section>

      {/* Store */}
      <section id="store" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="label-eyebrow">
              <span className="mandala-dot" />The Collection
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-[#2D2926] mt-3 tracking-tight">
              Available Works
            </h2>
          </div>
          <p className="hidden md:block text-sm text-[#5B5653] max-w-xs text-right">
            Once a painting is sold it leaves the collection. Every piece is one-of-one.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-20 label-eyebrow text-[#5B5653]">Loading…</div>
        ) : products.length === 0 ? (
          <div
            data-testid="empty-store"
            className="text-center py-20 border border-[#E3DDD7] bg-[#EFEBE6]/40"
          >
            <p className="label-eyebrow mb-4">Coming soon</p>
            <p className="text-[#5B5653] max-w-md mx-auto">
              The artist is currently preparing the next collection. Please check back soon.
            </p>
          </div>
        ) : (
          <div
            data-testid="product-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 lg:gap-16"
          >
            {products.map((p, i) => (
              <ArtworkCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* About teaser */}
      {artist.bio && (
        <section className="wash-cool border-y border-[#E5D8BD]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20 items-center">
            {artist.photo_path && (
              <div className="md:col-span-5 fade-up">
                <div className="bg-[#EFEBE6] aspect-[4/5] overflow-hidden max-w-md mx-auto md:mx-0">
                  <img
                    src={imageUrl(artist.photo_path)}
                    alt={artist.name || "The artist"}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
            <div className={artist.photo_path ? "md:col-span-7" : "md:col-span-12"}>
              <p className="label-eyebrow">About the Artist</p>
              <h2 className="font-serif text-3xl md:text-4xl font-light text-[#2D2926] mt-3 tracking-tight">
                {artist.name || "The Artist"}
              </h2>
              <p className="text-[#5B5653] leading-relaxed mt-6 max-w-2xl line-clamp-5">
                {artist.bio}
              </p>
              <Link
                to="/about"
                data-testid="about-link"
                className="inline-block mt-8 label-eyebrow text-[#E25C46] hover:text-[#C3402A]"
              >
                Read full story →
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
